// ==========================================================
// ALTERAÇÕES CONTRATUAIS - REGRAS E CÁLCULOS (seção 4)
// Funções puras usadas pelas telas. Quotas e valores aceitam
// casas decimais; as comparações usam tolerância para evitar
// diferenças de arredondamento.
// ==========================================================

export const TIPOS_MOVIMENTO = [
  { value: "AUMENTO_CAPITAL", label: "Aumento de capital", sinal: 1, transferencia: false },
  { value: "REDUCAO_CAPITAL", label: "Redução de capital", sinal: -1, transferencia: false },
  { value: "COMPRA", label: "Compra", sinal: 1, transferencia: true },
  { value: "VENDA", label: "Venda", sinal: -1, transferencia: true },
  { value: "DOACAO_RECEBIDA", label: "Doação recebida", sinal: 1, transferencia: true },
  { value: "DOACAO_ENVIADA", label: "Doação enviada", sinal: -1, transferencia: true },
  { value: "CESSAO", label: "Cessão", sinal: null, transferencia: true },
];

export const DIRECOES_CESSAO = [
  { value: "RECEBIDA", label: "Recebida" },
  { value: "CEDIDA", label: "Cedida" },
];

export const TIPOS_DOCUMENTO = [
  { value: "MINUTA", label: "Minuta" },
  { value: "ASSINADO", label: "Assinado" },
  { value: "REGISTRADO", label: "Registrado" },
];

export const TOLERANCIA = 0.000001;

export const labelTipoMovimento = (mov) => {
  const tipo = TIPOS_MOVIMENTO.find((t) => t.value === mov.tipo)?.label ?? mov.tipo;
  if (mov.tipo !== "CESSAO") return tipo;
  return `${tipo} ${mov.direcao === "CEDIDA" ? "cedida" : "recebida"}`;
};

export const labelTipoDocumento = (v) => TIPOS_DOCUMENTO.find((t) => t.value === v)?.label ?? v;

// +1 entra no sócio, -1 sai do sócio.
export function sinalMovimento(mov) {
  if (mov.tipo === "CESSAO") return mov.direcao === "CEDIDA" ? -1 : 1;
  return TIPOS_MOVIMENTO.find((t) => t.value === mov.tipo)?.sinal ?? 0;
}

// Converte "1.234,56" ou "1234.56" em número. Retorna null se inválido.
export function lerNumero(valor) {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  const texto = String(valor ?? "").trim();
  if (!texto) return null;
  const normalizado = texto.includes(",") ? texto.replace(/\./g, "").replace(",", ".") : texto;
  const numero = Number(normalizado);
  return Number.isFinite(numero) ? numero : null;
}

export const formatarNumero = (valor, casas = 2) =>
  valor === null || valor === undefined || !Number.isFinite(valor)
    ? "—"
    : valor.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: casas });

export const formatarMoeda = (valor) =>
  valor === null || valor === undefined || !Number.isFinite(valor) ? "—" : valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const formatarPercentual = (valor) =>
  valor === null || valor === undefined || !Number.isFinite(valor)
    ? "—"
    : `${valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;

// Alterações da empresa em ordem de número.
export const alteracoesDaEmpresa = (alteracoes, empresaId) =>
  alteracoes.filter((a) => String(a.empresaId) === String(empresaId)).sort((a, b) => a.numero - b.numero);

// Posição: Map socioId -> { quotas, valor }
function posicaoVazia() {
  return new Map();
}

function somarNaPosicao(posicao, socioId, quotas, valor) {
  const chave = String(socioId);
  const atual = posicao.get(chave) || { quotas: 0, valor: 0 };
  posicao.set(chave, { quotas: atual.quotas + quotas, valor: atual.valor + valor });
}

// Posição "antes" da alteração (regra 1): saldo final da alteração anterior
// da mesma empresa; na carga inicial, a posição digitada.
export function calcularPosicaoAntes(alteracao, alteracoes) {
  if (alteracao.cargaInicial) {
    const posicao = posicaoVazia();
    (alteracao.posicaoInicial || []).forEach((p) => somarNaPosicao(posicao, p.socioId, lerNumero(p.quotas) || 0, lerNumero(p.valor) || 0));
    return posicao;
  }
  const anteriores = alteracoesDaEmpresa(alteracoes, alteracao.empresaId).filter((a) => a.id !== alteracao.id && a.numero < alteracao.numero);
  const anterior = anteriores[anteriores.length - 1];
  return anterior ? calcularPosicaoDepois(anterior, alteracoes) : posicaoVazia();
}

// Posição "depois" = antes + movimentos (cada sócio).
export function calcularPosicaoDepois(alteracao, alteracoes) {
  const posicao = new Map(calcularPosicaoAntes(alteracao, alteracoes));
  (alteracao.movimentos || []).forEach((m) => {
    const sinal = sinalMovimento(m);
    somarNaPosicao(posicao, m.socioId, sinal * (lerNumero(m.quotas) || 0), sinal * (lerNumero(m.valor) || 0));
  });
  return posicao;
}

const totalizar = (posicao) => [...posicao.values()].reduce((t, p) => ({ quotas: t.quotas + p.quotas, valor: t.valor + p.valor }), { quotas: 0, valor: 0 });

// Linhas para a tabela de posição societária (antes x depois).
export function montarTabelaPosicao(alteracao, alteracoes) {
  const antes = calcularPosicaoAntes(alteracao, alteracoes);
  const depois = calcularPosicaoDepois(alteracao, alteracoes);
  const totalAntes = totalizar(antes);
  const totalDepois = totalizar(depois);
  const ids = [...new Set([...antes.keys(), ...depois.keys()])];

  const linha = (p, total) => {
    if (!p) return { quotas: 0, valor: 0, valorQuota: null, percentual: 0 };
    return {
      quotas: p.quotas,
      valor: p.valor,
      valorQuota: Math.abs(p.quotas) > TOLERANCIA ? p.valor / p.quotas : null,
      percentual: total.quotas > TOLERANCIA ? (p.quotas / total.quotas) * 100 : 0,
    };
  };

  const linhas = ids.map((socioId) => ({ socioId, antes: linha(antes.get(socioId), totalAntes), depois: linha(depois.get(socioId), totalDepois) }));

  return {
    linhas,
    totalAntes: { ...totalAntes, valorQuota: totalAntes.quotas > TOLERANCIA ? totalAntes.valor / totalAntes.quotas : null },
    totalDepois: { ...totalDepois, valorQuota: totalDepois.quotas > TOLERANCIA ? totalDepois.valor / totalDepois.quotas : null },
  };
}

// Validações da seção 4. Retorna lista de mensagens (vazia = ok).
export function validarAlteracao(alteracao, alteracoes) {
  const erros = [];
  const daEmpresa = alteracoesDaEmpresa(alteracoes, alteracao.empresaId).filter((a) => a.id !== alteracao.id);

  if (!alteracao.empresaId) erros.push("Selecione a empresa.");
  if (!Number.isInteger(alteracao.numero) || alteracao.numero < 0) erros.push("Informe o número da alteração (inteiro, 0 para constituição).");
  if (daEmpresa.some((a) => a.numero === alteracao.numero)) erros.push(`Já existe a alteração nº ${alteracao.numero} para esta empresa.`);
  if (daEmpresa.some((a) => a.numero > alteracao.numero))
    erros.push("Somente é possível lançar ou editar a última alteração da empresa (as posições são encadeadas).");

  if (alteracao.cargaInicial) {
    const linhas = alteracao.posicaoInicial || [];
    if (linhas.length === 0) erros.push("Carga inicial: informe a posição societária de partida (sócios, quotas e valores).");
    linhas.forEach((p) => {
      if (!p.socioId) erros.push("Carga inicial: há linha sem sócio.");
      if (lerNumero(p.quotas) === null || lerNumero(p.quotas) < 0) erros.push("Carga inicial: quotas inválidas.");
      if (lerNumero(p.valor) === null || lerNumero(p.valor) < 0) erros.push("Carga inicial: valor inválido.");
    });
    if (new Set(linhas.map((p) => String(p.socioId))).size !== linhas.length) erros.push("Carga inicial: sócio repetido.");
  }

  // Regra 4: tipo de movimento é lista fechada.
  (alteracao.movimentos || []).forEach((m, i) => {
    const n = i + 1;
    if (!m.socioId) erros.push(`Movimento ${n}: selecione o sócio.`);
    if (!TIPOS_MOVIMENTO.some((t) => t.value === m.tipo)) erros.push(`Movimento ${n}: tipo inválido.`);
    if (m.tipo === "CESSAO" && !DIRECOES_CESSAO.some((d) => d.value === m.direcao)) erros.push(`Movimento ${n}: informe se a cessão foi recebida ou cedida.`);
    const q = lerNumero(m.quotas);
    const v = lerNumero(m.valor);
    if (q === null || q <= 0) erros.push(`Movimento ${n}: quotas devem ser maiores que zero.`);
    if (v === null || v < 0) erros.push(`Movimento ${n}: valor inválido.`);
  });

  // Regra 2: transferências entre sócios precisam se compensar
  // (o que sai de um sócio entra em outro).
  let saldoTransferencias = 0;
  (alteracao.movimentos || []).forEach((m) => {
    const tipo = TIPOS_MOVIMENTO.find((t) => t.value === m.tipo);
    if (tipo?.transferencia) saldoTransferencias += sinalMovimento(m) * (lerNumero(m.quotas) || 0);
  });
  if (Math.abs(saldoTransferencias) > TOLERANCIA) {
    erros.push(
      `As transferências entre sócios não fecham: diferença de ${formatarNumero(saldoTransferencias, 6)} quota(s). O que sai de um sócio deve entrar em outro.`,
    );
  }

  // Nenhum sócio pode ficar negativo; a empresa precisa ter quotas.
  const depois = calcularPosicaoDepois(alteracao, alteracoes);
  depois.forEach((p) => {
    if (p.quotas < -TOLERANCIA) erros.push("Há sócio com quotas negativas após os movimentos.");
    if (p.valor < -TOLERANCIA) erros.push("Há sócio com valor negativo após os movimentos.");
  });
  const total = totalizar(depois);
  // Regra 3: % calculado sobre o total; exige total positivo para fechar 100%.
  if (total.quotas <= TOLERANCIA) erros.push("A posição final precisa ter quotas (a soma das participações deve fechar 100%).");

  return [...new Set(erros)];
}
